import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Tooltip from '@atlaskit/tooltip';

import { useEditionAwareness } from '../../services/edition-awareness-context';
import { useGetExperimentValue } from '../../services/experiment-context';
import { getLoggedInPage } from '../../services/get-logged-in-page';
import { isEligibleForEAtoOSBExperiment } from '../../services/is-eligible-for-ea-to-osb-experiment';
import { WAC_URL } from '../../services/wac-urls';
import { NoAction } from '../actions-and-content';
import { EditionAwarenessLink } from '../edition-awareness-button';

export const JiraStandardTrialOverviewLink = (): React.JSX.Element => {
	const context = useEditionAwareness();
	const { cloudId, environment, billingSourceSystem, userEmail, automationFreeLimitStatus } =
		context;
	const { getExperimentValue } = useGetExperimentValue();

	const handleJstoClick = useCallback(() => {
		if (automationFreeLimitStatus) {
			getExperimentValue({
				statsigParams: {
					experimentName: 'growth_automation_limits_experiment',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
				trackEventParameters: {
					trackEventGrowthInitiative: 'tako',
				},
			});
		}
		// Fire exposure for ea_to_osb_experiment control tenants
		if (isEligibleForEAtoOSBExperiment(context)) {
			getExperimentValue({
				statsigParams: {
					experimentName: 'ea_to_osb_experiment',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
				trackEventParameters: {
					trackEventGrowthInitiative: 'tako',
				},
			});
		}
	}, [automationFreeLimitStatus, getExperimentValue, context]);

	// This information should always be available, given the prior checks to get here
	if (!environment || !billingSourceSystem || !userEmail) {
		return <NoAction />;
	}

	const rawJstoUrl =
		`${WAC_URL[environment]}/software/jira?ref=edition_awareness_standard_trial_` +
		`${billingSourceSystem.toLowerCase()}&cloudSiteId=${cloudId}`;
	const jstoUrl = getLoggedInPage(userEmail, rawJstoUrl);

	return (
		<Tooltip
			content={
				<FormattedMessage
					id="@atlassian/edition-awareness.link.jira-standard-trial-overview-tooltip"
					defaultMessage="More about your trial and how to pay"
					description="Tooltip for trial information button"
				/>
			}
			position="bottom"
		>
			<EditionAwarenessLink
				href={jstoUrl}
				target="_blank"
				touchpointIntent="addPaymentDetails"
				linkId="jsto"
				onClick={handleJstoClick}
			/>
		</Tooltip>
	);
};
